import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

interface SessionStatus {
  status: "complete" | "expired" | "open" | string;
  customerEmail: string | null;
  planKey: string | null;
  isTest: boolean;
}

export default function Scale360CheckoutReturn() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id") ?? "";

  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) { setError("No session ID found."); return; }
    fetch(`/api/billing/scale360/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then((data: SessionStatus & { error?: string }) => {
        if (data.error) { setError(data.error); return; }
        setStatus(data);
      })
      .catch(() => setError("Unable to verify payment status. Please contact support."));
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      {/* Brand header */}
      <div
        className="w-full fixed top-0 left-0"
        style={{ background: "#111827" }}
      >
        <div className="max-w-5xl mx-auto px-4 py-4">
          <img
            src="https://scale360.wibiz.ai/wp-content/uploads/2026/03/scale-logo-1.png"
            alt="Scale360"
            className="h-8 w-auto object-contain"
          />
        </div>
      </div>
      <div className="mt-20" />

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 max-w-md w-full text-center">
        {!status && !error && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            <p className="text-gray-500">Verifying your payment…</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-12 h-12 text-red-400" />
            <h1 className="text-xl font-bold text-gray-900">Something went wrong</h1>
            <p className="text-sm text-gray-500">{error}</p>
            <a
              href="https://scale360.wibiz.ai/"
              className="mt-2 inline-block text-sm font-semibold hover:underline"
              style={{ color: "#E8620A" }}
            >
              ← Back to Scale360
            </a>
          </div>
        )}

        {status?.status === "complete" && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 className="w-14 h-14 text-green-500" />
            <h1 className="text-2xl font-bold text-gray-900">Welcome to Scale360!</h1>
            <p className="text-gray-600">
              Your subscription is confirmed.
              {status.customerEmail && (
                <>
                  {" "}A confirmation has been sent to{" "}
                  <span className="font-semibold">{status.customerEmail}</span>.
                </>
              )}
            </p>
            <p className="text-sm text-gray-500">
              Our team will reach out shortly to get you onboarded.
            </p>
            {status.isTest && (
              <span className="text-xs bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full">
                Test payment — no real charge
              </span>
            )}
            <a
              href="https://scale360.wibiz.ai/"
              className="mt-2 inline-block px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: "#E8620A" }}
            >
              Go to Scale360
            </a>
          </div>
        )}

        {status && status.status !== "complete" && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-12 h-12 text-amber-400" />
            <h1 className="text-xl font-bold text-gray-900">Payment not completed</h1>
            <p className="text-sm text-gray-500">
              Your payment was not processed. You have not been charged.
            </p>
            <a
              href="https://scale360.wibiz.ai/"
              className="mt-2 inline-block text-sm font-semibold hover:underline"
              style={{ color: "#E8620A" }}
            >
              ← Back to Scale360
            </a>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        Need help? Email us at{" "}
        <a href="mailto:support@bizconnect360.com" className="underline hover:text-gray-600">
          support@bizconnect360.com
        </a>
      </p>
    </div>
  );
}
